// LBTreeEditor.tsx

import React, { useCallback, useEffect, useState } from "react";
import { TreeView, TreeItem } from "@material-ui/lab";
import { Button, Grid, TextField, Select, MenuItem, Typography, FormControl, InputLabel, makeStyles, Checkbox } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { LoadBalancer } from "../../../shared/types/flexiCharging";
import { Charger } from "../../../shared/types/charger";
import EvStationIcon from "@material-ui/icons/EvStation";  // charger
import StorageIcon from "@material-ui/icons/Storage"
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
    root: {
        marginBottom: theme.spacing(0.1),
        // Fix for selection background color bleeding
        '&:focus > $content': {
            backgroundColor: theme.palette.action.selected,
        },
        // Target the TreeItem's selected state directly
        '&.Mui-selected > $content': {
            backgroundColor: theme.palette.action.selected,
        },
        '&.Mui-selected > $content $label': {
            backgroundColor: 'transparent', // Ensure label background doesn't interfere
        }
    },
    // Target the main content area
    content: {
        minHeight: 32, // Further reduced height for a tighter row
        padding: 0,
        // Ensure the selection/hover background covers the whole area
        '&.Mui-selected': {
            backgroundColor: theme.palette.action.selected,
        },
    },
    // Target the label span where text and icons are
    label: {
        // Only adjust padding on the label itself
        padding: theme.spacing(0.5, 1), // Tighter padding: 4px top/bottom, 8px left/right
        minHeight: 32,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
    },
    group: {},
    expanded: {},
    selected: {},
    iconContainer: {
        // By default, do nothing or apply a common style if needed

        // Target the iconContainer inside TreeItems that are expanded/collapsed
        // This ensures the style is applied ONLY to Load Balancer nodes (which have the collapse/expand icon)
        // You are implicitly keeping the original CSS by only defining the property you need to change.
        '&$expanded > $content $iconContainer': {
            marginRight: theme.spacing(0.5), // Example adjustment for expanded nodes
        },
        '&$group > $content $iconContainer': {
            // This is generally a better selector for the icon container of expandable nodes
            marginRight: theme.spacing(0.5),
        }
    },
    checkbox: {
        color: '#6100ED', // purple when enabled (unchecked)
        '&$checked': {
            color: '#6100ED', // purple when enabled (checked)
        },
        '&$disabled': {
            color: '#A0A0A0', // grey when disabled (unchecked)
        },
        '&$checked$disabled': {
            color: '#A0A0A0', // grey when disabled (checked)
        },
        marginBottom: '4px',
        padding: '0 8px 0 0',
    },
    checked: {},   // required for overrides
    disabled: {},  // required for overrides
}));

interface LoadBalancerEditorProps {
    loadBalancers: LoadBalancer[]
    chargers: Charger[]
    onCreate(): string;
    onEdit(type: "lb" | "charger", id: string, changes: any): void;
    onDelete(lbId: string): void
    errors?: Record<string, Partial<Record<keyof LoadBalancer, string>>>;
}

const LoadBalancerEditor: React.FC<LoadBalancerEditorProps> = ({ loadBalancers, chargers, onCreate, onEdit, onDelete, errors }) => {
    type Node = LoadBalancer | Charger;
    const classes = useStyles();
    const { t } = useTranslation()

    const [selectedLbId, setSelectedLbId] = useState<string | null>(null);
    const [selectedChargerId, setSelectedChargerId] = useState<string | null>(null);

    const rootNodes: Node[] = [
        ...loadBalancers.filter(lb => !lb.parentLbId),
        ...chargers.filter(c => !c.loadBalancerId) // treat unassigned chargers as roots
    ];

    const selectedLb = selectedLbId
        ? loadBalancers.find(lb => lb.id === selectedLbId) ?? null
        : null;

    const selectedCharger = selectedChargerId
        ? chargers.find(c => c.id === selectedChargerId) ?? null
        : null;

    const [expandedNodes, setExpandedNodes] = useState<string[]>(() =>
        loadBalancers.map(lb => lb.id)
    );

    const getDescendants = useCallback((lbId: string): string[] => {
        const children = loadBalancers.filter(lb => lb.parentLbId === lbId);
        return children.reduce<string[]>(
            (acc, child) => [...acc, child.id, ...getDescendants(child.id)],
            []
        );
    }, [loadBalancers]);

    // const handleManualFlexiChargingChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    //     if (selectedCharger) {
    //         onEdit("charger", selectedCharger.id, {
    //             manualFlexiCharging: event.target.checked
    //         });
    //     }
    // };

    const renderNode = useCallback((node: LoadBalancer | Charger): JSX.Element => {
        const isLb = "capacityKw" in node;
        const childNodes: JSX.Element[] = [];

        if (isLb) {
            const lbNode = node as LoadBalancer;

            // 1. Find and render child Load Balancers (recursive call)
            const childLbs = loadBalancers.filter(lb => lb.parentLbId === lbNode.id);
            childLbs.forEach(child => childNodes.push(renderNode(child)));

            // 2. Find and render assigned Chargers (base case)
            const assignedChargers = chargers.filter(c => c.loadBalancerId === lbNode.id);
            assignedChargers.forEach(charger => childNodes.push(renderNode(charger)));
        }

        // Note: For Charger nodes (isLb is false), childNodes remains empty, 
        // which is correct as chargers are leaf nodes.

        return (
            <TreeItem
                key={isLb ? node.id : `charger-${node.id}`}
                nodeId={isLb ? node.id : `charger-${node.id}`}

                // Apply the custom classes
                classes={classes}

                label={
                    <span className={classes.label}>
                        {isLb ? ( // Load Balancer Icon
                            <StorageIcon fontSize="small" style={{ marginRight: 4, color: '#3f51b5' }} />
                        ) : (
                            // Charger Icon
                            <EvStationIcon fontSize="small" style={{ marginRight: 4, color: '#ff9800' }} />
                        )}
                        {node.name}
                        {isLb && ` (${(node as any).capacityKw} kW)`}
                    </span>
                }
            >
                {/* THIS IS THE CRITICAL PART: Pass the children nodes to the TreeItem */}
                {childNodes.length > 0 ? childNodes : undefined}
            </TreeItem>
        );
    }, [loadBalancers, chargers, classes]);

    useEffect(() => {
        const lbExists = selectedLbId && loadBalancers.some(lb => lb.id === selectedLbId);

        if (selectedLbId && !lbExists) {
            setSelectedLbId(null);
            setSelectedChargerId(null);
            return;
        }

        if (selectedLbId && lbExists) {
            const selectedLb = loadBalancers.find(lb => lb.id === selectedLbId);

            if (selectedLb && selectedLb.parentLbId && !expandedNodes.includes(selectedLb.parentLbId)) {
                setExpandedNodes(prev => [...prev, selectedLb.parentLbId!]);
            }

            if (selectedChargerId) {
                setSelectedChargerId(null);
            }
        }
    }, [loadBalancers, selectedLbId, selectedChargerId, expandedNodes]);

    return (
        <Grid container spacing={3}>
            {/* LEFT: TREE */}
            <Grid item xs={6}>
                <Typography variant="h6">{t('locations.addLoadBalancer')}</Typography>
                <TreeView
                    defaultCollapseIcon={<ExpandMoreIcon />}
                    defaultExpandIcon={<ChevronRightIcon />}
                    multiSelect={false}
                    selected={selectedLbId || selectedChargerId ? `charger-${selectedChargerId}` : undefined}
                    expanded={expandedNodes}
                    onNodeToggle={(e, nodeIds) => setExpandedNodes(nodeIds)}
                    onNodeSelect={(e: any, nodeId: string) => {
                        if (nodeId.startsWith("charger-")) {
                            const chargerId = nodeId.replace("charger-", "");
                            setSelectedChargerId(chargerId);
                            setSelectedLbId(null); // clear LB selection
                        } else {
                            setSelectedLbId(nodeId);
                            setSelectedChargerId(null); // clear charger selection
                        }
                    }}
                >
                    {rootNodes.map(node => renderNode(node))}
                </TreeView>

                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                        const newId = onCreate(); // onCreate returns new LB id
                        setSelectedLbId(newId);
                    }}
                    style={{ marginTop: 12 }}
                >
                    {t('locations.addLoadBalancer')}
                </Button>
            </Grid>

            {/* RIGHT: DETAIL PANEL */}
            <Grid item xs={6}>
                {/* LB EDITOR */}
                {selectedLb && (
                    <>
                        <Typography variant="h6">{t('locations.loadBalancer')}</Typography>

                        <TextField
                            label={t('common.name')}
                            fullWidth
                            margin="normal"
                            value={selectedLb.name}
                            onChange={(e) => onEdit("lb", selectedLb.id, { name: e.target.value })}
                            error={!!errors?.[selectedLb.id]?.name}
                            helperText={errors?.[selectedLb.id]?.name}
                        />

                        <TextField
                            label={t('locations.capacity')}
                            type="number"
                            fullWidth
                            margin="normal"
                            // Ak je hodnota 0 a chceme dovoliť zmazanie, zobrazíme prázdny string
                            value={selectedLb.capacityKw === 0 ? '' : selectedLb.capacityKw}
                            onChange={(e) => {
                                const val = e.target.value;
                                // Ak používateľ všetko zmazal, nastavíme 0 (alebo null/undefined podľa tvojho uváženia)
                                const numericValue = val === '' ? 0 : Number(val);
                                onEdit("lb", selectedLb.id, { capacityKw: numericValue });
                            }}
                            error={!!errors?.[selectedLb.id]?.capacityKw}
                            helperText={errors?.[selectedLb.id]?.capacityKw}
                            // Tento prop zabezpečí, že sa pri type="number" nebudú dať písať nezmysly
                            InputLabelProps={{ shrink: true }}
                        />

                        <FormControl fullWidth margin="normal">
                            <InputLabel>{t('locations.parentLoadBalancer')}</InputLabel>
                            <Select
                                value={selectedLb.parentLbId || ""}
                                onChange={(e) => {
                                    onEdit("lb", selectedLb.id, { parentLbId: e.target.value || null })
                                }}
                            >
                                <MenuItem value="">(root)</MenuItem>

                                {loadBalancers
                                    .filter(lb =>
                                        lb.id !== selectedLb.id &&
                                        !getDescendants(selectedLb.id).includes(lb.id)
                                    )
                                    .map(lb => (
                                        <MenuItem key={lb.id} value={lb.id}>
                                            {lb.name}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => {
                                onDelete(selectedLb.id);
                                setSelectedLbId(null);
                            }}
                            style={{ marginTop: 16 }}
                        >
                            {t('common.delete')}
                        </Button>
                    </>
                )}

                {/* CHARGER EDITOR */}
                {selectedCharger && (
                    <>
                        <Typography variant="h6">{t('history.charger')}</Typography>

                        <TextField
                            label={t('common.name')}
                            fullWidth
                            disabled
                            margin="normal"
                            value={selectedCharger.name}
                        />

                        <FormControl fullWidth margin="normal">
                            <InputLabel>{t('locations.assignedLoadBalancer')}</InputLabel>
                            <Select
                                value={selectedCharger.loadBalancerId || ""}
                                onChange={(e) => onEdit("charger", selectedCharger.id, { loadBalancerId: e.target.value || null })}
                            >
                                <MenuItem value="">{t('locations.unassigned')}</MenuItem>
                                {loadBalancers.map(lb => (
                                    <MenuItem key={lb.id} value={lb.id}>
                                        {lb.name} ({lb.capacityKw} kW)
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Grid item xs={12}>
                            <Typography variant="caption" style={{ fontSize: '18px', color: '#000000' }}>
                                <Checkbox
                                    name="manualFlexiCharging"
                                    checked={!!selectedCharger.manualFlexiCharging}
                                    onChange={(e) => onEdit("charger", selectedCharger.id, { manualFlexiCharging: e.target.checked })}
                                    classes={{
                                        root: classes.checkbox,
                                        checked: classes.checked,
                                        disabled: classes.disabled,
                                    }}
                                />
                                {t('locations.manualFlexiCharging')}
                            </Typography>
                        </Grid>
                    </>
                )}

                {!selectedLb && !selectedCharger && (
                    <Typography>{t('locations.selectItemFromTree')}</Typography>
                )}
            </Grid>
        </Grid>
    );
}

export default LoadBalancerEditor;